import React, { useEffect, useState } from 'react';
import { Select, DatePicker, Button, InputNumber } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RelationChangeLogFiltersType } from '../graphql/queries/getRelationChangeLogs';
import { ENTITY_TYPE_OPTIONS, RELATION_TYPE_OPTIONS } from '../enums/RelationLogEnums';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface RelationChangeLogFilterProps {
    filters: RelationChangeLogFiltersType;
    onFilterChange: (filters: RelationChangeLogFiltersType) => void;
    onReset: () => void;
}

const RelationChangeLogFilter: React.FC<RelationChangeLogFilterProps> = ({
    filters,
    onFilterChange,
    onReset
}) => {
    const { t } = useTranslation();
    const [localFilters, setLocalFilters] = useState<RelationChangeLogFiltersType>(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleFilter = () => {
        onFilterChange(localFilters);
    };

    const handleReset = () => {
        setLocalFilters({});
        onReset();
    };

    return (
        <div style={{ 
            background: '#fff', 
            padding: '16px'
        }}>
            {/* 第一行字段 */}
            <div style={{ 
                display: 'flex', 
                gap: '16px', 
                alignItems: 'flex-end',
                marginBottom: '8px'
            }}>
                <div style={{ flex: '1', minWidth: '150px' }}>
                    <div style={{ marginBottom: '4px', fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                        {t('central.relationLogs.filters.entityType')}
                    </div>
                    <Select
                        value={localFilters.entityType}
                        placeholder={t('central.relationLogs.filters.entityTypePlaceholder') as string}
                        allowClear
                        style={{ width: '100%' }}
                        onChange={(value) => {
                            const newFilters = { ...localFilters, entityType: value || undefined };
                            setLocalFilters(newFilters);
                        }}
                    >
                        {ENTITY_TYPE_OPTIONS.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                                {t(option.label)}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                
                <div style={{ flex: '1', minWidth: '150px' }}>
                    <div style={{ marginBottom: '4px', fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                        {t('central.relationLogs.filters.entityId')}
                    </div>
                    <InputNumber
                        value={localFilters.entityId ? Number(localFilters.entityId) : undefined}
                        placeholder={t('central.relationLogs.filters.entityIdPlaceholder') as string}
                        style={{ width: '100%' }}
                        controls={false}
                        onChange={(value) => {
                            const newFilters = { ...localFilters, entityId: value ? String(value) : undefined };
                            setLocalFilters(newFilters);
                        }}
                    />
                </div>
                
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <div style={{ marginBottom: '4px', fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                        {t('central.relationLogs.filters.relationType')}
                    </div>
                    <Select
                        value={localFilters.relationType}
                        placeholder={t('central.relationLogs.filters.relationTypePlaceholder') as string}
                        allowClear
                        style={{ width: '100%' }}
                        onChange={(value) => {
                            const newFilters = { ...localFilters, relationType: value || undefined };
                            setLocalFilters(newFilters);
                        }}
                    >
                        {RELATION_TYPE_OPTIONS.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                                {t(option.label)}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                
                <div style={{ flex: '1', minWidth: '150px' }}>
                    <div style={{ marginBottom: '4px', fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                        {t('central.relationLogs.filters.operatorId')}
                    </div>
                    <InputNumber
                        value={localFilters.operatorId}
                        placeholder={t('central.relationLogs.filters.operatorIdPlaceholder') as string}
                        style={{ width: '100%' }}
                        controls={false}
                        onChange={(value) => {
                            const newFilters = { ...localFilters, operatorId: value || undefined };
                            setLocalFilters(newFilters);
                        }}
                    />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '80px' }}>
                    <Button 
                        type="primary" 
                        icon={<SearchOutlined />}
                        onClick={handleFilter}
                        style={{ marginBottom: '4px' }}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('central.relationLogs.filters.filter')}</span>
                    </Button>
                    <Button 
                        icon={<ReloadOutlined />}
                        onClick={handleReset}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('central.relationLogs.filters.reset')}</span>
                    </Button>
                </div>
            </div>
            
            {/* 第二行：时间范围 */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ flex: '0 0 400px' }}>
                    <div style={{ marginBottom: '4px', fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                        {t('central.relationLogs.filters.operationTime')}
                    </div>
                    <RangePicker
                        value={localFilters.startTime && localFilters.endTime 
                            ? [dayjs.unix(localFilters.startTime), dayjs.unix(localFilters.endTime)]
                            : undefined}
                        showTime
                        format="YYYY-MM-DD HH:mm"
                        style={{ width: '100%' }}
                        onChange={(dates) => {
                            const newFilters = {
                                ...localFilters,
                                startTime: dates?.[0] ? dayjs(dates[0]).startOf('minute').unix() : undefined,
                                endTime: dates?.[1] ? dayjs(dates[1]).endOf('minute').unix() : undefined,
                            };
                            setLocalFilters(newFilters);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default RelationChangeLogFilter;